import { Suspense, lazy, useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'

// Where a dent sits, on the aircraft type's 3D model or its flat
// silhouette. The same component serves the member app (look only) and the
// admin app (click to place); an `onPlace` handler is what makes it an
// editor.
//
// The 3D scene pulls in three.js, so it is a separate chunk, fetched only
// when a 3D view is actually shown. The 2D view is an <img> and some
// absolutely positioned dots, and stays in the main bundle.
const AircraftDentScene = lazy(() => import('./AircraftDentScene'))

export interface DentMarker {
  dentId: number
  x: number
  y: number
  // null for a point on the silhouette (x and y are image fractions);
  // otherwise the point is in the model's coordinate space.
  z: number | null
  // Shown on the marker and in the hover card; see dentMarkers().
  label?: number
  note?: string
  meta?: string
  photoUrl?: string | null
}

// Where a hovered 3D marker is on screen, in px from the viewer's corner.
export interface DentHover {
  dentId: number
  x: number
  y: number
  width: number
}

export interface DentPoint {
  x: number
  y: number
  z?: number
}

export interface DentSceneProps {
  modelUrl: string
  dents: DentMarker[]
  selectedId?: number | null
  onPlace?: (point: { x: number; y: number; z: number }) => void
  onSelect?: (dentId: number) => void
  onHover?: (hover: DentHover | null) => void
  onError?: () => void
}

interface AircraftDentViewProps {
  view: '3d' | '2d'
  // The .glb for '3d', the silhouette image for '2d'.
  assetUrl: string
  dents: DentMarker[]
  selectedId?: number | null
  onSelect?: (dentId: number) => void
  onPlace?: (point: DentPoint) => void
}

const DOT = 22

// The card that follows the pointer onto a marker: its number, the note
// and the first photo, if there is one.
function DentCard({ dent, left, top }: { dent: DentMarker; left: string; top: string }) {
  return (
    <Paper
      elevation={6}
      sx={{
        position: 'absolute',
        left,
        top,
        transform: 'translate(-50%, calc(-100% - 16px))',
        width: 'max-content',
        maxWidth: 240,
        p: 1,
        pointerEvents: 'none',
        zIndex: 2,
        lineHeight: 1.4,
      }}
    >
      {dent.label !== undefined ? (
        <Typography variant="subtitle2" component="div">
          #{dent.label}
        </Typography>
      ) : null}
      {dent.note ? (
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
          {dent.note}
        </Typography>
      ) : null}
      {dent.meta ? (
        <Typography variant="caption" color="text.secondary" component="div">
          {dent.meta}
        </Typography>
      ) : null}
      {dent.photoUrl ? (
        <Box component="img" src={dent.photoUrl} alt="" sx={{ display: 'block', mt: 0.5, maxWidth: '100%', maxHeight: 140, borderRadius: 0.5 }} />
      ) : null}
    </Paper>
  )
}

function AircraftDentView({ view, assetUrl, dents, selectedId, onSelect, onPlace }: AircraftDentViewProps) {
  const { t } = useTranslation()
  const [failed, setFailed] = useState(false)
  const [hover, setHover] = useState<DentHover | null>(null)
  const [hoverId, setHoverId] = useState<number | null>(null)

  if (failed) return <Alert severity="error">{t('aircraftDent.loadFailed')}</Alert>

  const hovered3d = hover ? dents.find((d) => d.dentId === hover.dentId) : undefined
  const shown2d = dents.find((d) => d.dentId === (hoverId ?? selectedId))

  if (view === '3d') {
    return (
      <Box sx={{ position: 'relative', width: '100%' }}>
        <Box sx={{ width: '100%', aspectRatio: '4 / 3', maxHeight: 480, borderRadius: 1, bgcolor: 'action.hover', overflow: 'hidden' }}>
          <Suspense fallback={<CircularProgress sx={{ m: 'auto', display: 'block', mt: 8 }} />}>
            <AircraftDentScene
              modelUrl={assetUrl}
              dents={dents}
              selectedId={selectedId}
              onPlace={onPlace}
              onSelect={onSelect}
              onHover={setHover}
              onError={() => setFailed(true)}
            />
          </Suspense>
        </Box>
        {hover && hovered3d ? (
          // Kept inside the viewer's width so it is never cut off at the edges.
          <DentCard
            dent={hovered3d}
            left={`${Math.min(Math.max(hover.x, 120), Math.max(hover.width - 120, 120))}px`}
            top={`${hover.y}px`}
          />
        ) : null}
      </Box>
    )
  }

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!onPlace) return
    const rect = event.currentTarget.getBoundingClientRect()
    const clamp = (n: number) => Math.min(1, Math.max(0, n))
    onPlace({ x: clamp((event.clientX - rect.left) / rect.width), y: clamp((event.clientY - rect.top) / rect.height) })
  }

  return (
    <Box
      onClick={handleClick}
      sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%', lineHeight: 0, cursor: onPlace ? 'crosshair' : 'default' }}
    >
      <Box
        component="img"
        src={assetUrl}
        alt={t('aircraftDent.silhouetteAlt')}
        onError={() => setFailed(true)}
        sx={{ maxWidth: '100%', maxHeight: 480, display: 'block', bgcolor: 'common.white', borderRadius: 1 }}
      />
      {dents
        .filter((d) => d.z === null)
        .map((d) => (
          <Box
            key={d.dentId}
            component="button"
            type="button"
            aria-label={t('aircraftDent.marker', { n: d.label ?? d.dentId })}
            onMouseEnter={() => setHoverId(d.dentId)}
            onMouseLeave={() => setHoverId(null)}
            onClick={(event) => {
              event.stopPropagation()
              onSelect?.(d.dentId)
            }}
            sx={{
              position: 'absolute',
              left: `${d.x * 100}%`,
              top: `${d.y * 100}%`,
              width: DOT * (d.dentId === selectedId ? 1.5 : 1),
              height: DOT * (d.dentId === selectedId ? 1.5 : 1),
              transform: 'translate(-50%, -50%)',
              borderRadius: '50%',
              border: '2px solid white',
              bgcolor: d.dentId === selectedId ? 'warning.main' : 'error.main',
              color: 'common.white',
              fontSize: 12,
              fontWeight: 700,
              lineHeight: 1,
              p: 0,
              cursor: 'pointer',
            }}
          >
            {d.label}
          </Box>
        ))}
      {shown2d && shown2d.z === null ? <DentCard dent={shown2d} left={`${shown2d.x * 100}%`} top={`${shown2d.y * 100}%`} /> : null}
    </Box>
  )
}

// The member app's read-only view.
export function AircraftDentViewer(props: Omit<AircraftDentViewProps, 'onPlace'>) {
  return <AircraftDentView {...props} />
}

// The admin app's view: a click on the aircraft hands back the point.
export function AircraftDentEditor(props: AircraftDentViewProps & { onPlace: (point: DentPoint) => void }) {
  return <AircraftDentView {...props} />
}
